import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import LockedTool from "@/components/LockedTool";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { renderMarkdown } from "@/lib/format";
import { Sparkles, Copy, Store, Loader2 } from "lucide-react";

const GENRES = [
  "Action",
  "Roguelike / Roguelite",
  "RPG",
  "Platformer",
  "Metroidvania",
  "Puzzle",
  "Strategy",
  "Shooter",
  "Survival",
  "Simulation",
  "Adventure",
  "Horror",
  "Deckbuilder",
];

const StoreCopy = () => {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [genre, setGenre] = useState("Roguelike / Roguelite");
  const [mechanics, setMechanics] = useState("");
  const [similar, setSimilar] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!title.trim() || !mechanics.trim()) {
      toast.error("Add at least a game title and key mechanics.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/generate/store-copy", {
        game_title: title,
        genre,
        mechanics,
        similar_games: similar,
      });
      setResult(res.data);
      toast.success("Store copy generated & saved to history.");
    } catch (e) {
      toast.error("Generation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  };

  if (!user?.is_pro) {
    return (
      <Layout title="Steam Store Page Copywriter" subtitle="Tool 02 // Wishlist-worthy listing copy">
        <LockedTool toolName="Steam Store Page Copywriter" />
      </Layout>
    );
  }

  return (
    <Layout title="Steam Store Page Copywriter" subtitle="Tool 02 // Wishlist-worthy listing copy">
      <div className="grid lg:grid-cols-2 gap-6" data-testid="store-copy-module">
        {/* Input */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Game Details</p>

          <div className="space-y-4">
            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Game Title</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="game-title-input"
                placeholder="e.g. Neon Abyss Reborn"
                className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Primary Genre</label>
              <Select value={genre} onValueChange={setGenre}>
                <SelectTrigger data-testid="genre-select" className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-teal-400">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 rounded-xl max-h-72">
                  {GENRES.map((g) => (
                    <SelectItem key={g} value={g} className="focus:bg-zinc-800 focus:text-teal-400 rounded-xl">
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Key Mechanics</label>
              <Input
                value={mechanics}
                onChange={(e) => setMechanics(e.target.value)}
                data-testid="mechanics-input"
                placeholder="e.g. dash-parry combat, procedural dungeons, permadeath"
                className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
            </div>

            <div>
              <label className="text-sm text-zinc-300 mb-2 block">Similar Games</label>
              <Input
                value={similar}
                onChange={(e) => setSimilar(e.target.value)}
                data-testid="similar-games-input"
                placeholder='e.g. "Hades meets Risk of Rain"'
                className="bg-zinc-950 border-zinc-800 rounded-xl focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
              />
            </div>
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-store-copy-btn"
            className="mt-6 w-full flex items-center justify-center gap-2 bg-teal-400 text-zinc-950 font-semibold py-3 rounded-xl hover:bg-teal-300 transition-colors duration-200 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed active:scale-[0.99]"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Generating…
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" /> Generate Store Copy
              </>
            )}
          </button>
        </div>

        {/* Output */}
        <div className="border border-zinc-800 bg-zinc-900/30 rounded-2xl p-5" data-testid="store-copy-output">
          {loading ? (
            <div className="space-y-3 pt-2">
              <Skeleton className="h-9 w-full bg-zinc-800 rounded-xl mb-4" />
              <Skeleton className="h-4 w-full bg-zinc-800 rounded-xl" />
              <Skeleton className="h-4 w-5/6 bg-zinc-800 rounded-xl" />
              <Skeleton className="h-4 w-2/3 bg-zinc-800 rounded-xl" />
              <Skeleton className="h-5 w-1/3 bg-zinc-800 rounded-xl mt-6" />
              <Skeleton className="h-4 w-full bg-zinc-800 rounded-xl" />
            </div>
          ) : result ? (
            <Tabs defaultValue="short">
              <TabsList className="bg-zinc-950 border border-zinc-800 rounded-xl p-1">
                <TabsTrigger
                  value="short"
                  data-testid="tab-short"
                  className="rounded-xl data-[state=active]:bg-teal-400 data-[state=active]:text-zinc-950 text-zinc-400 font-mono text-xs uppercase tracking-wider"
                >
                  Short Description
                </TabsTrigger>
                <TabsTrigger
                  value="detailed"
                  data-testid="tab-detailed"
                  className="rounded-xl data-[state=active]:bg-teal-400 data-[state=active]:text-zinc-950 text-zinc-400 font-mono text-xs uppercase tracking-wider"
                >
                  About This Game
                </TabsTrigger>
              </TabsList>

              <TabsContent value="short" className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`font-mono text-xs ${result.short.length <= 300 ? "text-teal-400" : "text-red-400"}`}
                    data-testid="short-char-count"
                  >
                    {result.short.length} / 300 chars
                  </span>
                  <button
                    onClick={() => copy(result.short, "Short description")}
                    data-testid="copy-short-btn"
                    className="flex items-center gap-1.5 text-xs border border-zinc-700 text-teal-400 rounded-xl px-3 py-1.5 hover:border-teal-400 hover:bg-teal-400/10 transition-colors duration-200"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                </div>
                <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 min-h-[260px]" data-testid="short-description">
                  <p className="text-zinc-200 leading-relaxed">{result.short}</p>
                </div>
              </TabsContent>

              <TabsContent value="detailed" className="mt-4">
                <div className="flex items-center justify-end mb-2">
                  <button
                    onClick={() => copy(result.detailed, "About This Game")}
                    data-testid="copy-detailed-btn"
                    className="flex items-center gap-1.5 text-xs border border-zinc-700 text-teal-400 rounded-xl px-3 py-1.5 hover:border-teal-400 hover:bg-teal-400/10 transition-colors duration-200"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy Markdown
                  </button>
                </div>
                <div
                  className="bg-zinc-950 border border-zinc-800 rounded-xl p-4 min-h-[260px] max-h-[460px] overflow-auto text-sm leading-relaxed"
                  data-testid="detailed-description"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(result.detailed) }}
                />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center">
              <Store className="h-8 w-8 text-zinc-700 mb-3" />
              <p className="text-zinc-600 text-sm font-mono">Short & detailed store copy will appear here.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StoreCopy;
